import { getAuth } from 'firebase/auth';
import { getApiUrl } from '../config/api';
import { doc, updateDoc, setDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface AffiliateData {
  id: string;
  userId?: string;
  email: string;
  affiliateId: string;
  createdAt?: Date;
  totalCommissions: number;
  count: number;
  isActive: boolean;
  isPartenaire?: boolean;
  users?: Array<{
    email: string;
    type: string;
    date?: any;
    customer_id?: string;
    totalCommissions?: number;
    lastPaymentDate?: any;
    status?: string;
  }>;
  commissionsHistory?: Array<{
    date: any;
    amount: number;
    type: string;
    from: string;
    interval?: string;
  }>;
  // Solde de paiement (cumulatif depuis le dernier paiement marqué "payé", ou depuis toujours)
  unpaidAmount?: number;
  lastPayoutAt?: any;
  pendingPayoutRequest?: {
    amount: number;
    requestedAt: any;
    paypalEmail?: string;
  } | null;
  // Champs partenaire
  firstName?: string;
  lastName?: string;
  diffusionMethods?: string[];
  audience?: string;
  paypalEmail?: string;
  acceptedTerms?: boolean;
  partnerProfileCompleted?: boolean;
  partnerProfileCreatedAt?: any;
  methodsInfo?: Record<string, string>;
}

export interface PartnerFormData {
  firstName: string;
  lastName: string;
  diffusionMethods: string[];
  audience: string;
  paypalEmail: string;
  acceptedTerms: boolean;
  methodsInfo: Record<string, string>;
}

export interface UserData {
  name: string;
  email: string;
  isPremium: boolean; 
}

export interface AffiliateResponse {
  success: boolean;
  affiliate: AffiliateData;
  user: UserData;
}

// Cache pour les données d'affiliation
const affiliateCache = {
  data: null as AffiliateResponse | null,
  lastFetch: 0,
  expirationTime: 5 * 60 * 1000 // 5 minutes
};

export const affiliateService = {
  // Fonction pour récupérer les données d'affiliation
  async getAffiliateData(forceRefresh = false): Promise<AffiliateResponse | null> {
    try {
      const now = Date.now();
      
      // Retourner les données du cache si elles sont encore valides
      if (!forceRefresh && 
          affiliateCache.data && 
          now - affiliateCache.lastFetch < affiliateCache.expirationTime) {
        return affiliateCache.data;
      }

      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('Must be logged in to fetch affiliate data');
      }

      const token = await user.getIdToken();
      
      const response = await fetch(getApiUrl('/api/user-affiliate'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Convertir les dates
      if (data.affiliate && data.affiliate.createdAt) {
        // Gérer les timestamps Firestore et les dates ISO
        const createdAtValue = data.affiliate.createdAt;
        if (createdAtValue._seconds) {
          // Timestamp Firestore
          data.affiliate.createdAt = new Date(createdAtValue._seconds * 1000);
        } else if (typeof createdAtValue === 'string' || typeof createdAtValue === 'number') {
          // Date ISO ou timestamp
          data.affiliate.createdAt = new Date(createdAtValue);
        } else {
          // Fallback - garder la valeur originale
          data.affiliate.createdAt = createdAtValue;
        }
      }
      
      // Mettre à jour le cache
      affiliateCache.data = data;
      affiliateCache.lastFetch = now;
      
      return data;
    } catch (error) {
      // console.error supprimé
      return null;
    }
  },

  // Étape 1 : démarre une demande de paiement (seuil global 50€) — envoie un code à 6 chiffres
  // par email, ne crée pas encore la demande tant que le code n'est pas confirmé.
  async requestPayout(): Promise<{ success: boolean; error?: string; requiresCode?: boolean; cooldownSeconds?: number }> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error('Must be logged in to request a payout');
      }

      const token = await user.getIdToken();

      const response = await fetch(getApiUrl('/api/affiliate-request-payout'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Erreur lors de la demande de paiement' };
      }

      return { success: true, requiresCode: data.requiresCode, cooldownSeconds: data.cooldownSeconds };
    } catch (error: any) {
      return { success: false, error: error?.message || 'Erreur lors de la demande de paiement' };
    }
  },

  // Renvoie un nouveau code de confirmation (bouton "Renvoyer" du modal de vérification)
  async resendPayoutCode(): Promise<{ success: boolean; error?: string; cooldownSeconds?: number }> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Must be logged in');
      const token = await user.getIdToken();

      const response = await fetch(getApiUrl('/api/affiliate-request-payout/resend-code'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.error || 'Erreur lors du renvoi du code' };
      }
      return { success: true, cooldownSeconds: data.cooldownSeconds };
    } catch (error: any) {
      return { success: false, error: error?.message || 'Erreur lors du renvoi du code' };
    }
  },

  // Étape 2 : confirme le code reçu par email → crée réellement la demande de paiement
  async verifyPayoutCode(code: string): Promise<{ success: boolean; error?: string; attemptsRemaining?: number; pendingPayoutRequest?: any }> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Must be logged in');
      const token = await user.getIdToken();

      const response = await fetch(getApiUrl('/api/affiliate-request-payout/verify-code'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.error || 'Code invalide', attemptsRemaining: data.attemptsRemaining };
      }

      this.invalidateCache();

      return { success: true, pendingPayoutRequest: data.pendingPayoutRequest };
    } catch (error: any) {
      return { success: false, error: error?.message || 'Erreur lors de la vérification du code' };
    }
  },

  // Fonction pour générer le lien d'affiliation
  generateAffiliateLink(affiliateId: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/?ref=${affiliateId}&code=FP20`;
  },

  // Fonction pour copier le lien d'affiliation
  async copyAffiliateLink(affiliateId: string): Promise<boolean> {
    try {
      const link = this.generateAffiliateLink(affiliateId);
      await navigator.clipboard.writeText(link);
      return true;
    } catch (error) {
      console.error('Erreur createPartnerProfile:', error);
      throw error;
    }
  },

  // Fonction pour valider un ID d'affilié
  async validateAffiliateId(affiliateId: string): Promise<{isValid: boolean; affiliateName?: string} | null> {
    try {
      const url = getApiUrl(`/api/validate-affiliate/${affiliateId}`);
      // console.log supprimé
      const response = await fetch(url);
      // console.log supprimé
      
      // Debug: regarder le contenu brut de la réponse
      const responseText = await response.text();
      // console.log supprimé
      
      if (!response.ok) {
        // console.log supprimé
        return null;
      }
      
      try {
        const data = JSON.parse(responseText);
        // console.log supprimé
        return data;
      } catch (parseError) {
        // console.error supprimé
        // console.log supprimé
        return null;
      }
    } catch (error) {
      // console.error supprimé
      return null;
    }
  },

  // Fonction pour récupérer les liens d'affiliation
  async getAffiliateLinks(affiliateId: string): Promise<{
    premiumMonthly: string;
    premiumAnnual: string;
    basicMonthly: string;
    basicAnnual: string;
  } | null> {
    try {
      const url = getApiUrl(`/api/affiliate-links/${affiliateId}`);
      // console.log supprimé
      
      const response = await fetch(url);
      // console.log supprimé
      
      if (!response.ok) {
        // console.log supprimé
        return null;
      }
      
      const data = await response.json();
      // console.log supprimé
      
      return data.links;
    } catch (error) {
      // console.error supprimé
      return null;
    }
  },

  // Fonction pour mettre à jour le statut partenaire
  async updatePartenaireStatus(isPartenaire: boolean): Promise<boolean> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('Must be logged in to update partner status');
      }

      // Rechercher le document d'affiliation de l'utilisateur
      const affiliatesRef = collection(db, 'affiliates');
      const q = query(affiliatesRef, where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('No affiliate document found for user');
      }

      // Mettre à jour le document d'affiliation
      const affiliateDoc = querySnapshot.docs[0];
      const affiliateDocRef = doc(db, 'affiliates', affiliateDoc.id);
      
      await updateDoc(affiliateDocRef, {
        isPartenaire: isPartenaire
      });

      // Invalider le cache pour forcer un refresh des données
      this.invalidateCache();
      
      return true;
    } catch (error) {
      console.error('Erreur createPartnerProfile:', error);
      throw error;
    }
  },

  // Fonction pour créer le profil partenaire
  async createPartnerProfile(formData: PartnerFormData): Promise<boolean> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('Must be logged in to create partner profile');
      }

      // Rechercher le document d'affiliation de l'utilisateur
      const affiliatesRef = collection(db, 'affiliates');
      const q = query(affiliatesRef, where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        // Créer un nouveau document d'affiliation si il n'existe pas
        // L'ID du document doit être l'UID de l'utilisateur (conforme aux règles Firestore)
        const newAffiliateId = this.generateAffiliateId();
        const newAffiliateRef = doc(db, 'affiliates', user.uid);
        
        await setDoc(newAffiliateRef, {
          userId: user.uid,
          email: user.email,
          affiliateId: newAffiliateId,
          isActive: true,
          isPartenaire: true,
          firstName: formData.firstName,
          lastName: formData.lastName,
          diffusionMethods: formData.diffusionMethods,
          audience: formData.audience,
          paypalEmail: formData.paypalEmail,
          acceptedTerms: formData.acceptedTerms,
          partnerProfileCompleted: true,
          partnerProfileCreatedAt: serverTimestamp(),
          methodsInfo: formData.methodsInfo,
          createdAt: serverTimestamp(),
          count: 0,
          totalCommissions: 0
        });
      } else {
        // Mettre à jour le document d'affiliation existant
        const affiliateDoc = querySnapshot.docs[0];
        const affiliateDocRef = doc(db, 'affiliates', affiliateDoc.id);
        
        await updateDoc(affiliateDocRef, {
          isPartenaire: true,
          firstName: formData.firstName,
          lastName: formData.lastName,
          diffusionMethods: formData.diffusionMethods,
          audience: formData.audience,
          paypalEmail: formData.paypalEmail,
          acceptedTerms: formData.acceptedTerms,
          partnerProfileCompleted: true,
          partnerProfileCreatedAt: serverTimestamp(),
          methodsInfo: formData.methodsInfo
        });
      }

      // Invalider le cache pour forcer un refresh des données
      this.invalidateCache();
      
      return true;
    } catch (error: any) {
      console.error('ERREUR createPartnerProfile:', error);
      console.error('Code:', error?.code);
      console.error('Message:', error?.message);
      throw error;
    }
  },

  // Générer un ID d'affilié unique
  generateAffiliateId(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  },

  // Fonction pour invalider le cache
  invalidateCache(): void {
    affiliateCache.data = null;
    affiliateCache.lastFetch = 0;
  }
};
